// The upload step's drag imitation (owner, 2026-09-23): a sample goes into the drop zone the way
// the user's own model will — dragged there — and then uploads like one. Mouse users drag it
// themselves (a click only shows how); touch and keyboard users get the drop done for them.

// What a dragged sample carries. Firefox starts no drag without data; the zone itself is told
// about the drag by the page, since Safari does not always expose custom types on dragover.
export const RENDER_SAMPLE_DRAG_TYPE = 'application/x-3dway-render-sample';

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// The middle of the part of `element` that is on screen, so a zone half scrolled away still
// catches the ghost where the user can see it.
export function visibleCenter(element: Element): { x: number; y: number } {
  const rect = element.getBoundingClientRect();
  const top = Math.max(rect.top, 0);
  const bottom = Math.min(rect.bottom, window.innerHeight);
  return {
    x: rect.left + rect.width / 2,
    y: bottom > top ? (top + bottom) / 2 : rect.top + rect.height / 2,
  };
}

// A copy of a sample's thumbnail travelling over the page (fixed, above everything, no pointer
// events). It removes itself at the end; `onLanded` runs just before, so whatever replaces it can
// appear in the same frame.
//   demo — lifts off the sample, glides to `to` and fades there: the gesture shown, nothing dropped;
//   drop — flies from the sample into `to` and lands at its size: the drop done for the user.
export function flyGhost(
  image: HTMLImageElement,
  to: { x: number; y: number; size?: number },
  mode: 'demo' | 'drop',
  onLanded?: () => void,
): Promise<void> {
  const from = image.getBoundingClientRect();
  const ghost = document.createElement('img');
  ghost.src = image.currentSrc || image.src;
  ghost.alt = '';
  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${from.width}px`,
    height: `${from.height}px`,
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid #000',
    background: '#080a0a',
    boxShadow: '0 14px 34px rgba(0, 0, 0, 0.28)',
    transformOrigin: '0 0',
    pointerEvents: 'none',
    zIndex: '1000',
  });

  const finish = (): void => {
    onLanded?.();
    ghost.remove();
  };
  if (typeof ghost.animate !== 'function') {
    finish();
    return Promise.resolve();
  }
  document.body.appendChild(ghost);

  // Scaled about its own centre, placed by its centre.
  const place = (x: number, y: number, scale: number): string =>
    `translate(${x - (from.width * scale) / 2}px, ${y - (from.height * scale) / 2}px) scale(${scale})`;
  const startX = from.left + from.width / 2;
  const startY = from.top + from.height / 2;
  const landing = to.size ? to.size / from.width : 1.1;

  const animation = mode === 'demo'
    ? ghost.animate([
      { offset: 0, transform: place(startX, startY, 1), opacity: 0 },
      { offset: 0.14, transform: place(startX, startY - 10, 1.12), opacity: 0.92 },
      { offset: 0.78, transform: place(to.x, to.y, 1.12), opacity: 0.92 },
      { offset: 1, transform: place(to.x, to.y, 0.9), opacity: 0 },
    ], { duration: 1500, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'forwards' })
    : ghost.animate([
      { offset: 0, transform: place(startX, startY, 1) },
      { offset: 0.2, transform: place(startX, startY - 12, 1.12) },
      { offset: 1, transform: place(to.x, to.y, landing) },
    ], { duration: 700, easing: 'cubic-bezier(0.3, 0, 0.2, 1)', fill: 'forwards' });

  return animation.finished.then(finish, finish);
}
